import { useState, useEffect, useCallback, useMemo } from "react";
import { useSearch } from "wouter";
import { ThemeProvider } from "@mui/material";
import { createBookingTheme } from "@/lib/theme";
import {
  getPharmacy,
  getAvailableSlots,
  createBooking,
  updateBookingDetails,
  SLOT_DURATION_OPTIONS,
  type BookingPharmacy,
  type TimeSlot,
  type PatientPreFill,
} from "@/lib/api";
import { CCS_CONDITIONS } from "@/lib/conditions";
import { localDateWindow, toLocalIsoDate } from "@/lib/dates";
import DefaultLandingPage from "./DefaultLandingPage";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Link,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

const PRIVACY_POLICY_URL = "https://www.katana-healthcare.com/privacy/pharm-assist";

type Step = "duration" | "date" | "time" | "condition" | "details" | "confirm" | "success";

interface PostBookingDetails {
  addressLine1: string;
  addressLine2: string;
  eircode: string;
  ppsn: string;
  medicalHistory: string;
  medications: string;
  allergies: string;
  gpName: string;
  gpPractice: string;
}

interface SnackState {
  message: string;
  severity: "error" | "success" | "info";
}

interface DetailsErrors {
  name?: string;
  dob?: string;
  email?: string;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isPlausibleDob(dob: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dob)) return false;
  const date = new Date(dob + "T00:00:00");
  if (Number.isNaN(date.getTime())) return false;
  const now = new Date();
  if (date > now) return false;
  const minDate = new Date(now.getFullYear() - 120, now.getMonth(), now.getDate());
  if (date < minDate) return false;
  return true;
}

function validatePatientDetails(patient: PatientPreFill): DetailsErrors {
  const errors: DetailsErrors = {};
  if (!patient.name.trim()) {
    errors.name = "Please enter your full name.";
  }
  if (!patient.dob.trim()) {
    errors.dob = "Please enter your date of birth.";
  } else if (!isPlausibleDob(patient.dob)) {
    errors.dob = "Please enter a valid date of birth.";
  }
  if (!patient.email.trim()) {
    errors.email = "Please enter your email address.";
  } else if (!EMAIL_REGEX.test(patient.email.trim())) {
    errors.email = "Please enter a valid email address.";
  }
  return errors;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr + "T00:00:00").toLocaleDateString("en-IE", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function formatTime(t: string): string {
  const [h, m] = t.split(":").map(Number);
  const isPM = h! >= 12;
  const hour12 = h! % 12 || 12;
  return `${hour12}:${String(m).padStart(2, "0")} ${isPM ? "PM" : "AM"}`;
}

function groupSlotsByDate(slots: TimeSlot[]): Record<string, TimeSlot[]> {
  const grouped: Record<string, TimeSlot[]> = {};
  for (const slot of slots) {
    if (!grouped[slot.date]) grouped[slot.date] = [];
    grouped[slot.date]!.push(slot);
  }
  return grouped;
}

// ── Demo mode data ────────────────────────────────────────────────────────────
const DEMO_PHARMACY: BookingPharmacy = {
  id: "demo",
  name: "CCS Pharmacy (Demo)",
  address: "14 Grafton Street, Dublin 2",
  eircode: "D02 Y689",
  primaryColor: "#7C3AED",
};

function computeEndTime(start: string, durationMinutes: number): string {
  const [h, m] = start.split(":").map(Number);
  const total = h! * 60 + m! + durationMinutes;
  const endH = Math.floor(total / 60) % 24;
  const endM = total % 60;
  return `${String(endH).padStart(2, "0")}:${String(endM).padStart(2, "0")}`;
}

function generateDemoSlots(durationMinutes: number): TimeSlot[] {
  const slots: TimeSlot[] = [];
  const now = new Date();
  const bookedTimes = new Set(["09:00-day1", "10:20-day1", "11:00-day2", "09:40-day3", "14:00-day4"]);
  let dayCount = 0;
  let i = 0;
  while (dayCount < 10 && i < 30) {
    const d = new Date(now);
    d.setDate(d.getDate() + i);
    i++;
    const dow = d.getDay();
    if (dow === 0 || dow === 6) continue;
    const dateStr = toLocalIsoDate(d);
    const times = [
      "09:00","09:20","09:40","10:00","10:20","10:40","11:00","11:20","11:40",
      "14:00","14:20","14:40","15:00","15:20","15:40","16:00","16:20","16:40",
    ];
    for (const t of times) {
      const end = computeEndTime(t, durationMinutes);
      const key = `${t}-day${dayCount}`;
      slots.push({ date: dateStr, startTime: t, endTime: end, available: !bookedTimes.has(key) });
    }
    dayCount++;
  }
  return slots;
}
// ─────────────────────────────────────────────────────────────────────────────

export default function BookingPage() {
  const search = useSearch();
  const params = new URLSearchParams(search);
  const pharmacyId = params.get("pharmacyId") ?? "";
  const prePharmacistId = params.get("pharmacistId") ?? "";
  const primaryColorParam = params.get("primaryColor") ?? "";

  // Demo/preview mode is opt-in only (?demo=true) — it must never trigger
  // automatically just because pharmacyId is missing, since that would make
  // the bare root fall into a fake booking flow.
  const isDemo = params.get("demo") === "true";

  const [pharmacy, setPharmacy] = useState<BookingPharmacy | null>(null);
  const [logoLoadFailed, setLogoLoadFailed] = useState(false);
  const [slotsByDate, setSlotsByDate] = useState<Record<string, TimeSlot[]>>({});
  // Distinct from "no dates came back": set only when the availability request
  // itself failed, so the date step can tell the two apart.
  const [slotsError, setSlotsError] = useState<string | null>(null);
  const [selectedDuration, setSelectedDuration] = useState<number | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [selectedCondition, setSelectedCondition] = useState<string>("");
  const [patient, setPatient] = useState<PatientPreFill>({
    name: "",
    dob: "",
    phone: "",
    email: "",
    symptomSummary: "",
  });
  const [step, setStep] = useState<Step>("duration");
  const [loading, setLoading] = useState(false);
  const [bookingId, setBookingId] = useState<string>("");
  // Capability token from the create-booking response — required to save the
  // optional post-booking details. Deliberately never persisted anywhere.
  const [detailsToken, setDetailsToken] = useState<string>("");
  const [postDetails, setPostDetails] = useState<PostBookingDetails>({
    addressLine1: "", addressLine2: "", eircode: "",
    ppsn: "", medicalHistory: "", medications: "",
    allergies: "", gpName: "", gpPractice: "",
  });
  const [detailsErrors, setDetailsErrors] = useState<DetailsErrors>({});
  const [detailsSaved, setDetailsSaved] = useState(false);
  const [savingDetails, setSavingDetails] = useState(false);
  const [snack, setSnack] = useState<SnackState | null>(null);

  const primaryColor =
    (primaryColorParam.startsWith("#") ? primaryColorParam : null) ??
    (pharmacy?.primaryColor?.startsWith("#") ? pharmacy.primaryColor : null) ??
    "#7C3AED"; // katanaPurple fallback

  const brandTheme = useMemo(() => createBookingTheme(primaryColor), [primaryColor]);

  const notify = (message: string, severity: SnackState["severity"] = "error") =>
    setSnack({ message, severity });

  const loadSlots = useCallback(async (durationMinutes: number) => {
    if (!pharmacyId && !isDemo) return;
    if (isDemo) {
      const demoSlots = generateDemoSlots(durationMinutes);
      setSlotsByDate(groupSlotsByDate(demoSlots.filter((s) => s.available)));
      return;
    }
    setLoading(true);
    setSlotsError(null);
    try {
      const days = localDateWindow(14);
      const fetched = await getAvailableSlots(
        pharmacyId,
        days[0]!,
        days[days.length - 1]!,
        durationMinutes,
        prePharmacistId || undefined,
      );
      setSlotsByDate(groupSlotsByDate(fetched.filter((s) => s.available)));
    } catch (err) {
      // getAvailableSlots (lib/api.ts) already logs the request/error detail
      // (component: "booking-widget") — log here too so a failed loadSlots
      // call is diagnosable from the browser console without reproducing.
      const message = err instanceof Error ? err.message : "Could not load available times.";
      console.error("[booking-widget] loadSlots failed", { pharmacyId, durationMinutes, message });
      // A failed request and a genuinely empty diary must never render the
      // same way: the transient snackbar disappears after a few seconds and
      // would leave the patient looking at "No availability" for a pharmacy
      // that may be wide open. Hold the failure in its own state so the date
      // step can say what happened and offer a retry.
      setSlotsByDate({});
      setSlotsError(message);
      notify("Could not load available times.", "error");
    } finally {
      setLoading(false);
    }
  }, [pharmacyId, isDemo, prePharmacistId]);

  useEffect(() => {
    if (!pharmacyId && !isDemo) return;
    setLogoLoadFailed(false);
    if (isDemo) {
      setPharmacy(DEMO_PHARMACY);
      return;
    }
    setLoading(true);
    getPharmacy(pharmacyId)
      .then((p) => setPharmacy(p))
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : "Could not load pharmacy details.";
        console.error("[booking-widget] getPharmacy failed", { pharmacyId, message });
        notify("Could not load pharmacy details.", "error");
      })
      .finally(() => setLoading(false));
  }, [pharmacyId, isDemo]);

  const handleSelectDuration = (duration: number) => {
    setSelectedDuration(duration);
    setStep("date");
    void loadSlots(duration);
  };

  const handleSelectDate = (date: string) => {
    setSelectedDate(date);
    setStep("time");
  };

  const handleSelectSlot = (slot: TimeSlot) => {
    setSelectedSlot(slot);
    setStep("condition");
  };

  const handleSelectCondition = (conditionId: string) => {
    setSelectedCondition(conditionId);
    setStep("details");
  };

  const handleSkipCondition = () => {
    setSelectedCondition("");
    setStep("details");
  };

  const handleDetailsNext = () => {
    const errors = validatePatientDetails(patient);
    setDetailsErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }
    setStep("confirm");
  };

  const handleConfirm = async () => {
    if (!selectedSlot) return;
    setLoading(true);
    try {
      if (isDemo) {
        await new Promise((r) => setTimeout(r, 900));
        setBookingId("DEMO-12345");
        setStep("success");
        return;
      }
      const result = await createBooking({
        pharmacyId,
        pharmacistId: prePharmacistId || undefined,
        condition: selectedCondition || undefined,
        date: selectedSlot.date,
        startTime: selectedSlot.startTime,
        endTime: selectedSlot.endTime,
        patient,
      });
      setBookingId(result.bookingId);
      setDetailsToken(result.detailsToken ?? "");
      setStep("success");
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Booking failed";
      notify(message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDetails = async () => {
    setSavingDetails(true);
    try {
      if (isDemo) {
        await new Promise((r) => setTimeout(r, 700));
        setDetailsSaved(true);
        return;
      }
      await updateBookingDetails({
        pharmacyId,
        bookingId,
        token: detailsToken,
        details: postDetails,
      });
      setDetailsSaved(true);
    } catch (err: unknown) {
      // Never claim success when the details were not actually saved —
      // this data is clinical/PII the pharmacist relies on being there.
      const message =
        err instanceof Error ? err.message : "Could not save your details.";
      notify(`${message} Please try again.`, "error");
    } finally {
      setSavingDetails(false);
    }
  };

  const condition = CCS_CONDITIONS.find((c) => c.id === selectedCondition);
  const availableDates = Object.keys(slotsByDate).sort();

  if (!pharmacyId && !isDemo) {
    return <DefaultLandingPage />;
  }

  return (
    <ThemeProvider theme={brandTheme}>
    <Box sx={{ minHeight: "100vh", bgcolor: "grey.50" }}>
      {isDemo && (
        <Box
          sx={{
            bgcolor: "#fffbeb",
            borderBottom: "1px solid #fde68a",
            px: 2,
            py: 1,
            textAlign: "center",
          }}
        >
          <Typography variant="body2" sx={{ color: "#92400e", fontWeight: 500 }}>
            ✦ Demo mode — this is a preview of the patient booking experience. No real booking will be made.
          </Typography>
        </Box>
      )}

      <Box
        sx={{ color: "white", px: 3, py: 4 }}
        style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}cc 100%)` }}
      >
        <Container maxWidth="md">
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {pharmacy?.logoUrl && !logoLoadFailed && (
              <Box
                component="img"
                src={pharmacy.logoUrl}
                alt={`${pharmacy.name} logo`}
                onError={() => setLogoLoadFailed(true)}
                sx={{
                  height: 44,
                  width: 44,
                  objectFit: "contain",
                  borderRadius: 1,
                  bgcolor: "rgba(255,255,255,0.15)",
                  p: 0.5,
                }}
              />
            )}
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {pharmacy?.name ?? "CCS Pharmacy"}
            </Typography>
          </Box>
          <Typography sx={{ opacity: 0.8, mt: 0.5 }}>Book a CCS consultation</Typography>
          {pharmacy && (
            <Typography variant="body2" sx={{ opacity: 0.7, mt: 0.5 }}>
              {pharmacy.address}
            </Typography>
          )}
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: 4 }}>
        {step !== "success" && (
          <StepIndicator currentStep={step} primaryColor={primaryColor} />
        )}

        {loading && step !== "success" && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress sx={{ color: primaryColor }} />
          </Box>
        )}

        {!loading && (
          <>
            {step === "duration" && (
              <DurationStep
                onSelect={handleSelectDuration}
                primaryColor={primaryColor}
              />
            )}
            {step === "date" && (
              <DateStep
                availableDates={availableDates}
                error={slotsError}
                onRetry={() => selectedDuration && void loadSlots(selectedDuration)}
                onSelect={handleSelectDate}
                onBack={() => setStep("duration")}
                primaryColor={primaryColor}
              />
            )}
            {step === "time" && (
              <TimeStep
                slots={slotsByDate[selectedDate] ?? []}
                date={selectedDate}
                onSelect={handleSelectSlot}
                onBack={() => setStep("date")}
                primaryColor={primaryColor}
              />
            )}
            {step === "condition" && (
              <ConditionStep
                onSelect={handleSelectCondition}
                onSkip={handleSkipCondition}
                onBack={() => setStep("time")}
                primaryColor={primaryColor}
              />
            )}
            {step === "details" && (
              <DetailsStep
                patient={patient}
                onChange={setPatient}
                errors={detailsErrors}
                onErrorsChange={setDetailsErrors}
                onNext={handleDetailsNext}
                onBack={() => setStep("condition")}
                primaryColor={primaryColor}
              />
            )}
            {step === "confirm" && selectedSlot && (
              <ConfirmStep
                pharmacy={pharmacy}
                slot={selectedSlot}
                condition={condition}
                patient={patient}
                onConfirm={handleConfirm}
                onBack={() => setStep("details")}
                primaryColor={primaryColor}
                loading={loading}
              />
            )}
            {step === "success" && (
              <SuccessStep
                bookingId={bookingId}
                patient={patient}
                slot={selectedSlot}
                pharmacy={pharmacy}
                primaryColor={primaryColor}
                postDetails={postDetails}
                onPostDetailsChange={setPostDetails}
                onSaveDetails={handleSaveDetails}
                savingDetails={savingDetails}
                detailsSaved={detailsSaved}
                isDemo={isDemo}
              />
            )}
          </>
        )}
      </Container>

      <Snackbar
        open={!!snack}
        onClose={() => setSnack(null)}
        autoHideDuration={4000}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snack?.severity} onClose={() => setSnack(null)}>
          {snack?.message}
        </Alert>
      </Snackbar>
    </Box>
    </ThemeProvider>
  );
}

function StepIndicator({ currentStep, primaryColor }: { currentStep: Step; primaryColor: string }) {
  const steps: Step[] = ["duration", "date", "time", "condition", "details", "confirm"];
  const labels = ["Duration", "Date", "Time", "Condition", "Your details", "Confirm"];
  const currentIdx = steps.indexOf(currentStep);

  return (
    <Box sx={{ display: "flex", alignItems: "center", mb: 4, gap: 0.5 }}>
      {steps.map((s, i) => (
        <Box key={s} sx={{ display: "flex", alignItems: "center", flex: 1 }}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              fontSize: "0.75rem",
              fontWeight: "bold",
              color: i <= currentIdx ? "white" : "text.disabled",
              bgcolor:
                i < currentIdx
                  ? "success.main"
                  : i === currentIdx
                  ? primaryColor
                  : "grey.300",
              transition: "background-color 0.2s",
            }}
          >
            {i < currentIdx ? <CheckIcon sx={{ fontSize: 14 }} /> : i + 1}
          </Box>
          {i < steps.length - 1 && (
            <Box
              sx={{
                height: 2,
                flex: 1,
                mx: 0.5,
                bgcolor: i < currentIdx ? "success.main" : "grey.300",
                transition: "background-color 0.2s",
              }}
            />
          )}
        </Box>
      ))}
    </Box>
  );
}

function DurationStep({
  onSelect,
  primaryColor,
}: {
  onSelect: (duration: number) => void;
  primaryColor: string;
}) {
  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
        How long do you need?
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Choose the length of your consultation.
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {SLOT_DURATION_OPTIONS.map((opt) => (
          <Paper
            key={opt.value}
            variant="outlined"
            onClick={() => onSelect(opt.value)}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 2,
              borderRadius: 2,
              cursor: "pointer",
              transition: "all 0.15s",
              "&:hover": { bgcolor: "action.hover", borderColor: primaryColor },
            }}
          >
            <Typography sx={{ fontWeight: 500 }}>{opt.label}</Typography>
            <ArrowForwardIcon sx={{ color: "text.disabled" }} />
          </Paper>
        ))}
      </Box>
    </Box>
  );
}

function DateStep({
  availableDates,
  error,
  onRetry,
  onSelect,
  onBack,
  primaryColor,
}: {
  availableDates: string[];
  error?: string | null;
  onRetry?: () => void;
  onSelect: (date: string) => void;
  onBack: () => void;
  primaryColor: string;
}) {
  // The availability request failed. This is NOT the same as an empty diary,
  // and must never be shown as one — the pharmacy may well be free, and the
  // patient needs something to act on rather than a dead end.
  if (error) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          size="small"
          sx={{ mb: 3, color: "text.secondary" }}
        >
          Back
        </Button>
        <Alert
          severity="error"
          action={
            onRetry ? (
              <Button color="inherit" size="small" onClick={onRetry}>
                Try again
              </Button>
            ) : undefined
          }
          sx={{ mb: 2 }}
        >
          We couldn&apos;t load available times
        </Alert>
        <Typography variant="body2" color="text.secondary">
          This is a problem on our side, not a full diary. Please try again — if it keeps
          happening, contact the pharmacy directly to book.
        </Typography>
      </Box>
    );
  }

  if (availableDates.length === 0) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          size="small"
          sx={{ mb: 3, color: "text.secondary" }}
        >
          Back
        </Button>
        <Box sx={{ textAlign: "center", py: 6 }}>
          <CalendarMonthIcon sx={{ fontSize: 40, color: "text.disabled", mb: 1 }} />
          <Typography sx={{ fontWeight: 500, mb: 0.5 }}>
            No availability
          </Typography>
          <Typography variant="body2" color="text.secondary">
            There are no available slots in the next 14 days. Please contact the pharmacy directly.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={onBack}
        size="small"
        sx={{ mb: 2, color: "text.secondary" }}
      >
        Back
      </Button>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
        Choose a date
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Select a date that suits you.
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {availableDates.map((date) => {
          const d = new Date(date + "T00:00:00");
          const dayName = d.toLocaleDateString("en-IE", { weekday: "long" });
          const dateStr = d.toLocaleDateString("en-IE", {
            day: "numeric",
            month: "long",
            year: "numeric",
          });
          return (
            <Paper
              key={date}
              variant="outlined"
              onClick={() => onSelect(date)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: 2,
                borderRadius: 2,
                cursor: "pointer",
                transition: "all 0.15s",
                "&:hover": { bgcolor: "action.hover", borderColor: primaryColor },
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 500 }}>{dayName}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {dateStr}
                </Typography>
              </Box>
              <ArrowForwardIcon sx={{ color: "text.disabled" }} />
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
}

function TimeStep({
  slots,
  date,
  onSelect,
  onBack,
  primaryColor,
}: {
  slots: TimeSlot[];
  date: string;
  onSelect: (slot: TimeSlot) => void;
  onBack: () => void;
  primaryColor: string;
}) {
  const morning = slots.filter((s) => parseInt(s.startTime) < 12);
  const afternoon = slots.filter((s) => parseInt(s.startTime) >= 12);

  if (slots.length === 0) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          size="small"
          sx={{ mb: 3, color: "text.secondary" }}
        >
          Back
        </Button>
        <Box sx={{ textAlign: "center", py: 6 }}>
          <AccessTimeIcon sx={{ fontSize: 40, color: "text.disabled", mb: 1 }} />
          <Typography sx={{ fontWeight: 500, mb: 0.5 }}>
            No slots available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            All times for this date are taken. Please go back and choose another date.
          </Typography>
        </Box>
      </Box>
    );
  }

  const SlotGroup = ({ label, group }: { label: string; group: TimeSlot[] }) =>
    group.length === 0 ? null : (
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", mb: 1.5 }}
        >
          {label}
        </Typography>
        <Grid container spacing={1}>
          {group.map((slot) => (
            <Grid size={{ xs: 4, sm: 3 }} key={`${slot.startTime}-${slot.endTime}`}>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => onSelect(slot)}
                sx={{ borderRadius: 2, fontWeight: "medium", fontSize: "0.875rem" }}
              >
                {formatTime(slot.startTime)}
              </Button>
            </Grid>
          ))}
        </Grid>
      </Box>
    );

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={onBack}
        size="small"
        sx={{ mb: 2, color: "text.secondary" }}
      >
        Back
      </Button>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
        Choose a time
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {formatDate(date)}
      </Typography>
      <SlotGroup label="Morning" group={morning} />
      <SlotGroup label="Afternoon" group={afternoon} />
    </Box>
  );
}

function ConditionStep({
  onSelect,
  onSkip,
  onBack,
  primaryColor,
}: {
  onSelect: (id: string) => void;
  onSkip: () => void;
  onBack: () => void;
  primaryColor: string;
}) {
  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={onBack}
        size="small"
        sx={{ mb: 2, color: "text.secondary" }}
      >
        Back
      </Button>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
        What are you coming in for?
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Select the condition you'd like assessed — this helps your pharmacist prepare.
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {CCS_CONDITIONS.map((c) => (
          <Paper
            key={c.id}
            variant="outlined"
            onClick={() => onSelect(c.id)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              p: 2,
              borderRadius: 2,
              cursor: "pointer",
              transition: "all 0.15s",
              "&:hover": { bgcolor: "action.hover", borderColor: primaryColor },
            }}
          >
            <Typography sx={{ fontSize: "1.5rem", flexShrink: 0 }}>
              {c.icon}
            </Typography>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {c.label}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {c.description}
              </Typography>
            </Box>
          </Paper>
        ))}
      </Box>
      <Button
        onClick={onSkip}
        fullWidth
        sx={{ mt: 2, color: "text.secondary" }}
      >
        Not sure yet — I'll discuss at the pharmacy →
      </Button>
    </Box>
  );
}

function DetailsStep({
  patient,
  onChange,
  errors,
  onErrorsChange,
  onNext,
  onBack,
  primaryColor,
}: {
  patient: PatientPreFill;
  onChange: (p: PatientPreFill) => void;
  errors: DetailsErrors;
  onErrorsChange: (e: DetailsErrors) => void;
  onNext: () => void;
  onBack: () => void;
  primaryColor: string;
}) {
  const updateField = (field: "name" | "dob" | "email", value: string) => {
    const nextPatient = { ...patient, [field]: value };
    onChange(nextPatient);
    if (!errors[field]) return;
    const fieldErrors = validatePatientDetails(nextPatient);
    if (!fieldErrors[field]) {
      const { [field]: _removed, ...rest } = errors;
      onErrorsChange(rest);
    }
  };

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={onBack}
        size="small"
        sx={{ mb: 2, color: "text.secondary" }}
      >
        Back
      </Button>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
        Your details
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Just the basics — this only takes a moment.
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <TextField
          label="Full name *"
          value={patient.name}
          onChange={(e) => updateField("name", e.target.value)}
          placeholder="e.g. Jane Smith"
          fullWidth
          size="small"
          error={!!errors.name}
          helperText={errors.name}
        />
        <TextField
          label="Date of birth *"
          type="date"
          value={patient.dob}
          onChange={(e) => updateField("dob", e.target.value)}
          fullWidth
          size="small"
          slotProps={{ inputLabel: { shrink: true } }}
          error={!!errors.dob}
          helperText={errors.dob}
        />
        <TextField
          label="Email address *"
          type="email"
          value={patient.email}
          onChange={(e) => updateField("email", e.target.value)}
          placeholder="e.g. jane@example.com"
          fullWidth
          size="small"
          error={!!errors.email}
          helperText={errors.email}
        />
        <TextField
          label="Phone number"
          type="tel"
          value={patient.phone}
          onChange={(e) => onChange({ ...patient, phone: e.target.value })}
          placeholder="e.g. 087 123 4567"
          fullWidth
          size="small"
        />
        <Typography variant="caption" color="text.secondary">
          Your data is shared only with the pharmacist for your consultation and is handled in
          accordance with GDPR. See our{" "}
          <Link href={PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer" color="inherit">
            privacy policy
          </Link>{" "}
          for how we use it and your rights.
        </Typography>
        <Button
          variant="contained"
          onClick={onNext}
          fullWidth
          size="large"
          endIcon={<ArrowForwardIcon />}
          style={{ background: primaryColor }}
        >
          Review booking
        </Button>
      </Box>
    </Box>
  );
}

function ConfirmStep({
  pharmacy,
  slot,
  condition,
  patient,
  onConfirm,
  onBack,
  primaryColor,
  loading,
}: {
  pharmacy: BookingPharmacy | null;
  slot: TimeSlot;
  condition?: { label: string; icon: string };
  patient: PatientPreFill;
  onConfirm: () => void;
  onBack: () => void;
  primaryColor: string;
  loading: boolean;
}) {
  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={onBack}
        size="small"
        sx={{ mb: 2, color: "text.secondary" }}
      >
        Back
      </Button>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
        Confirm your booking
      </Typography>
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 3 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Row label="Pharmacy" value={pharmacy?.name ?? "—"} />
          <Row label="Date" value={formatDate(slot.date)} />
          <Row
            label="Time"
            value={`${formatTime(slot.startTime)} – ${formatTime(slot.endTime)}`}
          />
          {condition && (
            <Row label="Condition" value={`${condition.icon} ${condition.label}`} />
          )}
          <Box sx={{ borderTop: "1px solid", borderColor: "divider", pt: 1.5 }} />
          <Row label="Name" value={patient.name} />
          <Row label="Date of birth" value={patient.dob} />
          <Row label="Email" value={patient.email} />
          {patient.phone && <Row label="Phone" value={patient.phone} />}
        </Box>
      </Paper>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        You'll receive an email confirmation with a calendar invite. You can cancel or reschedule anytime using the link in that email.
      </Typography>
      <Button
        variant="contained"
        onClick={onConfirm}
        fullWidth
        size="large"
        disabled={loading}
        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
        style={{ background: primaryColor }}
      >
        {loading ? "Confirming…" : "Confirm booking"}
      </Button>
    </Box>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500, textAlign: "right" }}>
        {value}
      </Typography>
    </Box>
  );
}

function SuccessStep({
  bookingId,
  patient,
  slot,
  pharmacy,
  primaryColor,
  postDetails,
  onPostDetailsChange,
  onSaveDetails,
  savingDetails,
  detailsSaved,
  isDemo,
}: {
  bookingId: string;
  patient: PatientPreFill;
  slot: TimeSlot | null;
  pharmacy: BookingPharmacy | null;
  primaryColor: string;
  postDetails: PostBookingDetails;
  onPostDetailsChange: (d: PostBookingDetails) => void;
  onSaveDetails: () => void;
  savingDetails: boolean;
  detailsSaved: boolean;
  isDemo: boolean;
}) {
  const set =
    (field: keyof PostBookingDetails) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      onPostDetailsChange({ ...postDetails, [field]: e.target.value });

  return (
    <Box>
      {/* Confirmation */}
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: 3,
          }}
          style={{ background: `${primaryColor}20` }}
        >
          <CheckCircleOutlineIcon sx={{ fontSize: 40, color: primaryColor }} />
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          Booking confirmed!
        </Typography>
        {slot && (
          <Typography color="text.secondary" sx={{ fontWeight: 500, mb: 0.5 }}>
            {formatDate(slot.date)} at {formatTime(slot.startTime)}
          </Typography>
        )}
        {pharmacy && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {pharmacy.name} · {pharmacy.address}
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          A confirmation email has been sent to <strong>{patient.email}</strong> with a calendar invite and manage links.
        </Typography>
        <Chip
          label={`Ref #${bookingId.slice(-8).toUpperCase()}`}
          variant="outlined"
          size="small"
          sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}
        />
      </Box>

      {/* Post-booking details form */}
      <Paper
        variant="outlined"
        sx={{ mt: 3, borderRadius: 3, p: 3, borderStyle: "dashed", borderWidth: 2 }}
      >
        {detailsSaved ? (
          <Box sx={{ textAlign: "center", py: 2 }}>
            <Typography sx={{ fontSize: "1.5rem", mb: 1 }}>
              🙌
            </Typography>
            <Typography sx={{ fontWeight: 500, mb: 0.5 }}>
              Thank you!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Your pharmacist will have these details ready before your appointment.
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 3 }}>
              <Typography sx={{ fontSize: "1.5rem", mt: 0.5 }}>
                ⚡
              </Typography>
              <Box>
                <Typography sx={{ fontWeight: 500 }}>Speed up your consultation</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Adding a few extra details now means your pharmacist can prepare in advance, keeping your appointment quick and focused. This is completely optional — you can always do it later.
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Address line 1"
                    value={postDetails.addressLine1}
                    onChange={set("addressLine1")}
                    placeholder="e.g. 14 Oak Road"
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Address line 2"
                    value={postDetails.addressLine2}
                    onChange={set("addressLine2")}
                    placeholder="e.g. Ranelagh"
                    fullWidth
                    size="small"
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    label="Eircode"
                    value={postDetails.eircode}
                    onChange={set("eircode")}
                    placeholder="e.g. D06 W5X9"
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    label="PPS number"
                    value={postDetails.ppsn}
                    onChange={set("ppsn")}
                    placeholder="e.g. 1234567T"
                    fullWidth
                    size="small"
                  />
                </Grid>
              </Grid>
              <TextField
                label="GP name & practice"
                value={postDetails.gpName}
                onChange={set("gpName")}
                placeholder="e.g. Dr. Murphy, Ranelagh Medical"
                fullWidth
                size="small"
              />
              <TextField
                label="Current medications"
                value={postDetails.medications}
                onChange={set("medications")}
                placeholder="List any medications you're currently taking (optional)"
                fullWidth
                multiline
                rows={2}
                size="small"
              />
              <TextField
                label="Known allergies"
                value={postDetails.allergies}
                onChange={set("allergies")}
                placeholder="e.g. Penicillin, pollen (optional)"
                fullWidth
                multiline
                rows={2}
                size="small"
              />
              <TextField
                label="Relevant medical history"
                value={postDetails.medicalHistory}
                onChange={set("medicalHistory")}
                placeholder="Any conditions, recent illnesses, or context that may be relevant (optional)"
                fullWidth
                multiline
                rows={3}
                size="small"
              />

              <Button
                variant="contained"
                onClick={onSaveDetails}
                disabled={savingDetails}
                fullWidth
                size="large"
                startIcon={
                  savingDetails ? <CircularProgress size={16} color="inherit" /> : undefined
                }
                style={{ background: primaryColor }}
              >
                {savingDetails ? "Saving…" : "Save details"}
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
                All fields are optional. Your data is handled in accordance with GDPR — see our{" "}
                <Link
                  href={PRIVACY_POLICY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  color="inherit"
                >
                  privacy policy
                </Link>
                .
              </Typography>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
}
